import type { DemoScript } from "../types.js";

/**
 * Settings → Tasks › deadline-derived urgency: auto urgency measures the
 * distance between a task's deadline (here its due day) and TODAY. The demo
 * points out today's column, then DRAGS the task closer twice — each drop
 * moves the matrix urgency column and the block's priority color. Days view
 * (anchored on today) so every column and cross-day drag is always on-grid;
 * thresholds are pinned (high ≤1, medium ≤3) so the caption numbers stay
 * truthful even if the defaults ever change.
 */
export const settingDeadlineUrgency: DemoScript = {
  id: "setting-deadline-urgency",
  titleKey: "demos.title.deadlineUrgency",
  view: { kind: "days+matrix", count: 5, focusRef: "taxes" },
  stage: { width: 1000, height: 620 },
  settings: async (ctx) => {
    await ctx.stores.settings.setMatrixScope("tasks");
    await ctx.stores.settings.setDeadlineUrgency(true);
    await ctx.stores.settings.setDeadlineUrgencyHighDays(1);
    await ctx.stores.settings.setDeadlineUrgencyMediumDays(3);
  },
  fixture: [
    {
      ref: "taxes",
      titleKey: "demos.fx.taxes",
      dayOffset: 4,
      hour: 10,
      durationMin: 90,
      matrixPos: { urgency: 0.15, importance: 0.75, urgencyAuto: true },
    },
  ],
  steps: [
    { kind: "caption", key: "demos.cap.deadline.intro" },
    { kind: "moveTo", target: { kind: "dayColumn", dayOffset: 0, hour: 10 }, captionKey: "demos.cap.deadline.today" },
    { kind: "moveTo", target: { kind: "task", ref: "taxes" }, captionKey: "demos.cap.deadline.far" },
    { kind: "press" },
    { kind: "dragTo", target: { kind: "dayColumn", dayOffset: 2, hour: 10 }, captionKey: "demos.cap.deadline.drag" },
    { kind: "release" },
    {
      kind: "action",
      run: (ctx) => ctx.stores.tasks.reschedule(ctx.id("taxes"), ctx.at(2, 10)),
      captionKey: "demos.cap.deadline.medium",
    },
    { kind: "moveTo", target: { kind: "matrixCell", col: 1, row: 2 } },
    { kind: "moveTo", target: { kind: "task", ref: "taxes" } },
    { kind: "press" },
    { kind: "dragTo", target: { kind: "dayColumn", dayOffset: 1, hour: 10 } },
    { kind: "release" },
    {
      kind: "action",
      run: (ctx) => ctx.stores.tasks.reschedule(ctx.id("taxes"), ctx.at(1, 10)),
      captionKey: "demos.cap.deadline.high",
    },
    { kind: "moveTo", target: { kind: "matrixCell", col: 2, row: 2 } },
    { kind: "moveTo", target: { kind: "task", ref: "taxes" }, captionKey: "demos.cap.deadline.tracked" },
    { kind: "pause", ms: 700 },
  ],
};
